import { useRef } from 'react';
import { Bold, Italic, List, Quote } from 'lucide-react';

interface TemplateVariable {
  name: string;
  description: string;
  example: string;
}

interface MarkdownTemplateEditorProps {
  value: string;
  onChange: (value: string) => void;
  variables: TemplateVariable[];
  placeholder?: string;
  label?: string;
}

export default function MarkdownTemplateEditor({
  value,
  onChange,
  variables,
  placeholder,
  label,
}: MarkdownTemplateEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertAtCursor = (textToInsert: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newValue = value.slice(0, start) + textToInsert + value.slice(end);
    onChange(newValue);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + textToInsert.length, start + textToInsert.length);
    }, 0);
  };

  const toolbarItems = [
    { icon: Bold, label: '粗體', prefix: '**', suffix: '**' },
    { icon: Italic, label: '斜體', prefix: '_', suffix: '_' },
    { icon: List, label: '列表', prefix: '• ', suffix: '' },
    { icon: Quote, label: '引用', prefix: '> ', suffix: '' },
  ];

  const handleToolbarClick = (prefix: string, suffix: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selectedText = value.slice(start, end);
    const newText = prefix + (selectedText || '文字') + suffix;
    const newValue = value.slice(0, start) + newText + value.slice(end);
    onChange(newValue);
    setTimeout(() => {
      el.focus();
      const cursorPos = start + prefix.length + (selectedText ? selectedText.length : 2);
      el.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const renderPreview = (content: string, vars: TemplateVariable[]) => {
    let html = content
      .replace(/\n/g, '<br/>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/_(.+?)_/g, '<em>$1</em>');

    for (const v of vars) {
      html = html.replace(
        new RegExp(`\\{\\{${v.name}\\}\\}`, 'g'),
        `<span class="bg-orange-100 text-orange-700 px-1 rounded text-xs font-mono">${v.example}</span>`
      );
    }

    return html;
  };

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-gray-700">{label}</label>
      )}

      <div className="flex flex-col md:flex-row gap-4">
        {/* Left column: Editor */}
        <div className="w-full md:w-[60%] space-y-2">
          {/* Toolbar */}
          <div className="flex items-center gap-2 flex-wrap">
            {toolbarItems.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => handleToolbarClick(item.prefix, item.suffix)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-50 text-gray-600 hover:bg-brand-50 hover:text-brand-600 border border-gray-200 text-xs font-medium transition-colors"
                title={item.label}
              >
                <item.icon className="w-3.5 h-3.5" />
                <span>{item.label}</span>
              </button>
            ))}
            <span className="text-[11px] text-gray-400 ml-1">Enter 直接換行</span>
          </div>

          {/* Variable pills */}
          {variables.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {variables.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  onClick={() => insertAtCursor(`{{${v.name}}}`)}
                  title={`${v.description}，例如：${v.example}`}
                  className="px-2 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200 text-xs font-mono hover:bg-orange-100 transition-colors"
                >
                  {'{{' + v.name + '}}'}
                </button>
              ))}
            </div>
          )}

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm font-mono leading-relaxed outline-none focus:border-brand-400 resize-y"
            style={{ minHeight: 200 }}
          />
        </div>

        {/* Right column: Preview */}
        <div className="w-full md:w-[40%]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">預覽</span>
          </div>
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4">
            {/* Phone mockup */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 max-w-sm mx-auto">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <div className="w-4 h-4 rounded-full bg-gray-300" />
                </div>
                <div className="flex-1">
                  <div className="h-2.5 w-20 bg-gray-200 rounded" />
                </div>
              </div>
              <div
                className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap"
                dangerouslySetInnerHTML={{
                  __html: renderPreview(value, variables),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
